/**
 * Permission Matrix Viewer
 * Visual tool to view and audit all permissions in the system
 * Accessible only to Management Admin users
 */

import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, X, Shield, Eye, EyeOff, Lock, Edit2, Settings } from "lucide-react";
import { api, toast } from "../App";
import FieldManager from "./FieldManager";
import {
  PAGE_ACCESS,
  FIELD_PERMISSIONS,
  ACTION_PERMISSIONS,
  DEPARTMENTS,
  ACCESS_LEVELS,
  SENSITIVITY
} from "../config/permissions";

export default function PermissionMatrix({ user }) {
  const [selectedModule, setSelectedModule] = useState('projects');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedAccessLevel, setSelectedAccessLevel] = useState('all');
  const [editMode, setEditMode] = useState(false);
  const [fieldManagerMode, setFieldManagerMode] = useState(false);
  const [pageConfig, setPageConfig] = useState(PAGE_ACCESS);
  const [fieldConfig, setFieldConfig] = useState(FIELD_PERMISSIONS);
  const [actionConfig, setActionConfig] = useState(ACTION_PERMISSIONS);

  // Check if user can access this page (Management and Admin)
  const isManagementAdmin = user?.department === 'Management' && user?.access_level === 'Admin';
  const isManagement = user?.department === 'Management';
  const canEdit = isManagementAdmin;
  
  if (!isManagement) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <Lock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h2 className="text-md3-headline-large mb-2">Access Denied</h2>
            <p className="text-md3-on-surface-variant">Only Management users can view the permission matrix.</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Load saved configurations
  useEffect(() => {
    loadSavedConfigs();
  }, []);

  const loadSavedConfigs = async () => {
    try {
      const [pageRes, fieldRes, actionRes] = await Promise.all([
        api.get('/permissions/config/page'),
        api.get('/permissions/config/field'),
        api.get('/permissions/config/action')
      ]);

      if (pageRes.data?.config && Object.keys(pageRes.data.config).length > 0) {
        setPageConfig(pageRes.data.config);
      }
      if (fieldRes.data?.config && Object.keys(fieldRes.data.config).length > 0) {
        setFieldConfig(fieldRes.data.config);
      }
      if (actionRes.data?.config && Object.keys(actionRes.data.config).length > 0) {
        setActionConfig(actionRes.data.config);
      }
    } catch (error) {
      console.error('Failed to load configurations:', error);
    }
  };

  const handleSavePageConfig = async (config) => {
    try {
      await api.post('/permissions/config/page', config);
      setPageConfig(config);
      toast.success('Page permissions saved successfully');
    } catch (error) {
      toast.error('Failed to save page permissions');
      throw error;
    }
  };

  const handleSaveFieldConfig = async (config) => {
    try {
      await api.post('/permissions/config/field', config);
      setFieldConfig(config);
      toast.success('Field permissions saved successfully');
    } catch (error) {
      toast.error('Failed to save field permissions');
      throw error;
    }
  };

  const handleSaveActionConfig = async (config) => {
    try {
      await api.post('/permissions/config/action', config);
      setActionConfig(config);
      toast.success('Action permissions saved successfully');
    } catch (error) {
      toast.error('Failed to save action permissions');
      throw error;
    }
  };

  const getSensitivityColor = (level) => {
    switch (level) {
      case SENSITIVITY.PUBLIC: return 'bg-green-100 text-green-800';
      case SENSITIVITY.INTERNAL: return 'bg-blue-100 text-blue-800';
      case SENSITIVITY.CONFIDENTIAL: return 'bg-yellow-100 text-yellow-800';
      case SENSITIVITY.RESTRICTED: return 'bg-red-100 text-red-800';
      default: return 'bg-md3-surface-container text-md3-on-surface';
    }
  };

  const getSensitivityIcon = (level) => {
    switch (level) {
      case SENSITIVITY.PUBLIC: return <Eye className="h-4 w-4" />;
      case SENSITIVITY.INTERNAL: return <Eye className="h-4 w-4" />;
      case SENSITIVITY.CONFIDENTIAL: return <Shield className="h-4 w-4" />;
      case SENSITIVITY.RESTRICTED: return <Lock className="h-4 w-4" />;
      default: return <Eye className="h-4 w-4" />;
    }
  };

  const checkPageAccess = (pagePath, dept, level) => {
    const pageConfig = PAGE_ACCESS[pagePath];
    if (!pageConfig) return true;

    if (pageConfig.allowed === 'all') return true;

    if (Array.isArray(pageConfig.allowed)) {
      return pageConfig.allowed.some(rule => {
        const deptMatch = rule.department === '*' || rule.department === dept;
        const levelMatch = rule.level === '*' || 
          (Array.isArray(rule.level) ? rule.level.includes(level) : rule.level === level);
        return deptMatch && levelMatch;
      });
    }

    return false;
  };

  const checkFieldAccess = (module, field, dept, level) => {
    const moduleFields = FIELD_PERMISSIONS[module];
    if (!moduleFields) return true;

    const fieldConfig = moduleFields[field];
    if (!fieldConfig) return true;

    if (fieldConfig.sensitivity === SENSITIVITY.PUBLIC) return true;
    if (fieldConfig.sensitivity === SENSITIVITY.INTERNAL) return true;

    if (fieldConfig.allowedRoles) {
      return fieldConfig.allowedRoles.some(rule => {
        const deptMatch = rule.department === '*' || rule.department === dept;
        const levelMatch = rule.level === '*' || 
          (Array.isArray(rule.level) ? rule.level.includes(level) : rule.level === level);
        return deptMatch && levelMatch;
      });
    }

    return false;
  };

  const checkActionPermission = (module, action, dept, level) => {
    const moduleActions = ACTION_PERMISSIONS[module];
    if (!moduleActions) return false;

    const actionConfig = moduleActions[action];
    if (!actionConfig) return false;

    if (actionConfig === 'all') return true;

    if (Array.isArray(actionConfig)) {
      return actionConfig.some(rule => {
        const deptMatch = rule.department === '*' || rule.department === dept;
        const levelMatch = rule.level === '*' || 
          (Array.isArray(rule.level) ? rule.level.includes(level) : rule.level === level);
        return deptMatch && levelMatch;
      });
    }

    return false;
  };

  // Filter departments and levels based on selection
  const getFilteredDepartments = () => {
    if (selectedDepartment !== 'all') {
      return [selectedDepartment];
    }
    return Object.values(DEPARTMENTS);
  };

  const getFilteredLevels = () => {
    if (selectedAccessLevel !== 'all') {
      return [selectedAccessLevel];
    }
    return Object.values(ACCESS_LEVELS);
  };

  // Handle cell click in edit mode for Page Access
  const handleCellClick = async (pagePath, dept, level, currentConfig) => {
    if (!editMode) return;
    
    const hasAccess = checkPageAccess(pagePath, dept, level);
    
    // Toggle access
    let newConfig = { ...currentConfig };
    let allowedRoles = newConfig[pagePath]?.allowed || [];
    
    // Handle 'all' case - convert to array with all dept/level combinations when editing
    if (allowedRoles === 'all') {
      // Convert 'all' to explicit array of all department/level combinations
      allowedRoles = [];
      Object.values(DEPARTMENTS).forEach(department => {
        allowedRoles.push({ department, level: '*' });
      });
    }
    
    if (!Array.isArray(allowedRoles)) {
      allowedRoles = [];
    }

    if (hasAccess) {
      // Remove access
      allowedRoles = allowedRoles.filter(rule => {
        if (rule.department !== dept && rule.department !== '*') return true;
        
        if (rule.level === '*') {
          return false; // Remove wildcard for simplicity
        } else if (Array.isArray(rule.level)) {
          const filtered = rule.level.filter(l => l !== level);
          return filtered.length > 0;
        } else if (rule.level === level) {
          return false;
        }
        return true;
      });
    } else {
      // Add access
      const existingRuleIndex = allowedRoles.findIndex(r => r.department === dept);
      
      if (existingRuleIndex >= 0) {
        const rule = allowedRoles[existingRuleIndex];
        if (Array.isArray(rule.level)) {
          if (!rule.level.includes(level)) {
            allowedRoles[existingRuleIndex] = {
              ...rule,
              level: [...rule.level, level]
            };
          }
        } else {
          allowedRoles[existingRuleIndex] = {
            ...rule,
            level: [rule.level, level]
          };
        }
      } else {
        allowedRoles.push({ department: dept, level: level });
      }
    }

    newConfig[pagePath] = {
      ...newConfig[pagePath],
      allowed: allowedRoles
    };

    setPageConfig(newConfig);
    await handleSavePageConfig(newConfig);
    toast.success(hasAccess ? 'Access removed' : 'Access granted');
  };

  // Handle cell click in edit mode for Field Visibility
  const handleFieldCellClick = async (module, field, dept, level, currentConfig) => {
    if (!editMode) return;
    
    const hasAccess = checkFieldAccess(module, field, dept, level);
    
    // Toggle access
    let newConfig = { ...currentConfig };
    
    // Ensure module exists
    if (!newConfig[module]) {
      newConfig[module] = {};
    }
    
    // Ensure field exists
    if (!newConfig[module][field]) {
      newConfig[module][field] = {
        sensitivity: SENSITIVITY.INTERNAL,
        allowedRoles: []
      };
    }
    
    let allowedRoles = newConfig[module][field].allowedRoles || [];
    
    if (!Array.isArray(allowedRoles)) {
      allowedRoles = [];
    }

    if (hasAccess) {
      // Remove access
      allowedRoles = allowedRoles.filter(rule => {
        if (rule.department !== dept && rule.department !== '*') return true;
        
        if (rule.level === '*') {
          return false;
        } else if (Array.isArray(rule.level)) {
          const filtered = rule.level.filter(l => l !== level);
          return filtered.length > 0;
        } else if (rule.level === level) {
          return false;
        }
        return true;
      });
    } else {
      // Add access
      const existingRuleIndex = allowedRoles.findIndex(r => r.department === dept);
      
      if (existingRuleIndex >= 0) {
        const rule = allowedRoles[existingRuleIndex];
        if (Array.isArray(rule.level)) {
          if (!rule.level.includes(level)) {
            allowedRoles[existingRuleIndex] = {
              ...rule,
              level: [...rule.level, level]
            };
          }
        } else {
          allowedRoles[existingRuleIndex] = {
            ...rule,
            level: [rule.level, level]
          };
        }
      } else {
        allowedRoles.push({ department: dept, level: level });
      }
    }

    newConfig[module][field] = {
      ...newConfig[module][field],
      allowedRoles: allowedRoles
    };

    setFieldConfig(newConfig);
    await handleSaveFieldConfig(newConfig);
    toast.success(hasAccess ? 'Access removed' : 'Access granted');
  };

  // Handle cell click in edit mode for Action Permissions
  const handleActionCellClick = async (module, action, dept, level, currentConfig) => {
    if (!editMode) return;
    
    const hasPermission = checkActionPermission(module, action, dept, level);
    
    // Toggle access
    let newConfig = { ...currentConfig };
    
    // Ensure module exists
    if (!newConfig[module]) {
      newConfig[module] = {};
    }
    
    // Ensure action exists
    if (!newConfig[module][action]) {
      newConfig[module][action] = [];
    }
    
    let allowedRoles = newConfig[module][action];
    
    if (!Array.isArray(allowedRoles)) {
      allowedRoles = [];
    }

    if (hasPermission) {
      // Remove access
      allowedRoles = allowedRoles.filter(rule => {
        if (rule.department !== dept && rule.department !== '*') return true;
        
        if (rule.level === '*') {
          return false;
        } else if (Array.isArray(rule.level)) {
          const filtered = rule.level.filter(l => l !== level);
          return filtered.length > 0;
        } else if (rule.level === level) {
          return false;
        }
        return true;
      });
    } else {
      // Add access
      const existingRuleIndex = allowedRoles.findIndex(r => r.department === dept);
      
      if (existingRuleIndex >= 0) {
        const rule = allowedRoles[existingRuleIndex];
        if (Array.isArray(rule.level)) {
          if (!rule.level.includes(level)) {
            allowedRoles[existingRuleIndex] = {
              ...rule,
              level: [...rule.level, level]
            };
          }
        } else {
          allowedRoles[existingRuleIndex] = {
            ...rule,
            level: [rule.level, level]
          };
        }
      } else {
        allowedRoles.push({ department: dept, level: level });
      }
    }

    newConfig[module][action] = allowedRoles;

    setActionConfig(newConfig);
    await handleSaveActionConfig(newConfig);
    toast.success(hasPermission ? 'Permission removed' : 'Permission granted');
  };

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-md3-display-small mb-2">Permission Matrix</h1>
            <p className="text-md3-on-surface-variant">
              {fieldManagerMode ? 'Add and manage modules and fields with sensitivity levels' :
               editMode ? 'Click cells to toggle permissions' : 
               'Visual overview of all access control rules'}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            {canEdit && (
              <>
                <Button
                  variant={fieldManagerMode ? "default" : "outline"}
                  onClick={() => {
                    setFieldManagerMode(!fieldManagerMode);
                    setEditMode(false);
                  }}
                >
                  <Settings className="mr-2 h-4 w-4" />
                  {fieldManagerMode ? 'Exit Field Manager' : 'Field Manager'}
                </Button>
                <Button
                  variant={editMode ? "default" : "outline"}
                  onClick={() => {
                    setEditMode(!editMode);
                    setFieldManagerMode(false);
                  }}
                >
                  <Edit2 className="mr-2 h-4 w-4" />
                  {editMode ? 'View Mode' : 'Edit Mode'}
                </Button>
                <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                  onClick={async () => {
                    if (!confirm('⚠️ This will apply the current permission matrix configuration to ALL users based on their department and access level.\n\n✅ Users will get permissions according to the matrix you configured.\n\n⚠️ Users must log out and log back in to see changes.\n\nContinue?')) return;
                    try {
                      const response = await api.post('/users/refresh-permissions');
                      toast.success(`✅ ${response.data.updated_count} users updated! All permissions now match the matrix. Users must log out and log back in.`);
                    } catch (error) {
                      toast.error('Failed to refresh permissions: ' + (error.response?.data?.detail || error.message));
                    }
                  }}
                >
                  <Shield className="mr-2 h-4 w-4" />
                  Apply to All Users
                </Button>
              </>
            )}
            <Badge variant="outline" className="text-lg">
              <Shield className="mr-2 h-4 w-4" />
              {isManagementAdmin ? 'Admin' : 'Management'}
            </Badge>
          </div>
        </div>

        {/* Filters - Show in both View and Edit modes */}
        {!fieldManagerMode && (
          <Card className="mt-4 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 ">
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-md3-body-small font-medium ">Filter by Department</Label>
                  <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                    <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {Object.values(DEPARTMENTS).map(dept => (
                        <SelectItem key={dept} value={dept}>
                          {dept}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-md3-body-small font-medium ">Filter by Access Level</Label>
                  <Select value={selectedAccessLevel} onValueChange={setSelectedAccessLevel}>
                    <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Levels</SelectItem>
                      {Object.values(ACCESS_LEVELS).map(level => (
                        <SelectItem key={level} value={level}>
                          {level}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {(selectedDepartment !== 'all' || selectedAccessLevel !== 'all') && (
                  <div className="flex items-end">
                    <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" 
                      onClick={() => {
                        setSelectedDepartment('all');
                        setSelectedAccessLevel('all');
                      }}
                    >
                      Clear Filters
                    </Button>
                  </div>
                )}
              </div>
              
              {editMode && (
                <div className="mt-3 space-y-2">
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-md3-lg">
                    <p className="text-md3-body-small text-blue-800">
                      <strong>💡 Edit Mode Active:</strong> Click any cell to toggle permission. Changes are saved immediately to the matrix. 
                      {selectedDepartment === 'all' && selectedAccessLevel === 'all' 
                        ? ' Apply filters to narrow down the view and edit specific roles.'
                        : ' Changes will only affect the filtered combinations.'}
                    </p>
                  </div>
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-md3-lg">
                    <p className="text-md3-body-small text-amber-800">
                      <strong>⚠️ Important:</strong> After editing the matrix, click <strong>"Apply to All Users"</strong> button above to push changes to all existing users. New users will automatically get permissions from this matrix.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {fieldManagerMode ? (
          <div className="mt-6">
            <FieldManager
              fieldConfig={fieldConfig}
              onSave={handleSaveFieldConfig}
            />
          </div>
        ) : (
        <Tabs defaultValue="pages" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="pages">Page Access</TabsTrigger>
            <TabsTrigger value="fields">Field Visibility</TabsTrigger>
            <TabsTrigger value="actions">Action Permissions</TabsTrigger>
            <TabsTrigger value="summary">Summary</TabsTrigger>
          </TabsList>

          {/* Page Access Matrix */}
          <TabsContent value="pages" className="space-y-6">
            <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
              <CardHeader>
                <CardTitle className="text-md3-title-large text-md3-on-surface ">Page Access Matrix</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table className="bg-md3-surface-container rounded-md3-md">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[200px]">Page</TableHead>
                        {getFilteredDepartments().map(dept => (
                          <TableHead key={dept} colSpan={getFilteredLevels().length} className="text-center border-l">
                            {dept}
                          </TableHead>
                        ))}
                      </TableRow>
                      <TableRow key="header-levels">
                        <TableHead></TableHead>
                        {getFilteredDepartments().map(dept => (
                          getFilteredLevels().map(level => (
                            <TableHead key={`${dept}-${level}`} className="text-xs">
                              {level === 'Team Member' ? 'TM' : level === 'Team Lead' ? 'TL' : level === 'Manager' ? 'MGR' : 'ADM'}
                            </TableHead>
                          ))
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.keys(pageConfig).map(pagePath => (
                        <TableRow key={pagePath}>
                          <TableCell className="font-medium">{pagePath}</TableCell>
                          {getFilteredDepartments().map(dept => (
                            getFilteredLevels().map(level => {
                              const hasAccess = checkPageAccess(pagePath, dept, level);
                              const isClickable = editMode && canEdit;
                              return (
                                <TableCell 
                                  key={`${pagePath}-${dept}-${level}`} 
                                  className={`text-center ${isClickable ? 'cursor-pointer hover:bg-md3-surface-container' : ''}`}
                                  onClick={() => isClickable && handleCellClick(pagePath, dept, level, pageConfig)}
                                  title={isClickable ? `Click to ${hasAccess ? 'remove' : 'grant'} ${dept}-${level} access` : ''}
                                >
                                  {hasAccess ? (
                                    <Check className="h-4 w-4 text-green-600 mx-auto" />
                                  ) : (
                                    <X className="h-4 w-4 text-red-400 mx-auto" />
                                  )}
                                </TableCell>
                              );
                            })
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="mt-4 flex gap-4 text-md3-body-small text-md3-on-surface-variant">
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    <span>Has Access</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <X className="h-4 w-4 text-red-400" />
                    <span>No Access</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Field Visibility Matrix */}
          <TabsContent value="fields" className="space-y-6">
            <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-md3-title-medium text-md3-on-surface">
                  <span>Field Visibility Matrix</span>
                  <Select value={selectedModule} onValueChange={setSelectedModule}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(fieldConfig).map(module => (
                        <SelectItem key={module} value={module}>
                          {module}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table className="bg-md3-surface-container rounded-md3-md">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[200px]">Field Name</TableHead>
                        <TableHead>Sensitivity</TableHead>
                        {getFilteredDepartments().map(dept => (
                          <TableHead key={dept} colSpan={getFilteredLevels().length} className="text-center border-l">
                            {dept}
                          </TableHead>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableHead></TableHead>
                        <TableHead></TableHead>
                        {getFilteredDepartments().map(dept => (
                          getFilteredLevels().map(level => (
                            <TableHead key={`${dept}-${level}`} className="text-xs">
                              {level === 'Team Member' ? 'TM' : level === 'Team Lead' ? 'TL' : level === 'Manager' ? 'MGR' : 'ADM'}
                            </TableHead>
                          ))
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {fieldConfig[selectedModule] && 
                        Object.entries(fieldConfig[selectedModule]).map(([fieldName, config]) => (
                        <TableRow key={fieldName}>
                          <TableCell className="font-medium">{fieldName}</TableCell>
                          <TableCell>
                            <Badge className={`${getSensitivityColor(config.sensitivity)} flex items-center gap-1 w-fit`}>
                              {getSensitivityIcon(config.sensitivity)}
                              {config.sensitivity}
                            </Badge>
                          </TableCell>
                          {getFilteredDepartments().map(dept => (
                            getFilteredLevels().map(level => {
                              const hasAccess = checkFieldAccess(selectedModule, fieldName, dept, level);
                              const isClickable = editMode && canEdit;
                              return (
                                <TableCell 
                                  key={`${fieldName}-${dept}-${level}`} 
                                  className={`text-center ${isClickable ? 'cursor-pointer hover:bg-md3-surface-container' : ''}`}
                                  onClick={() => isClickable && handleFieldCellClick(selectedModule, fieldName, dept, level, fieldConfig)}
                                  title={isClickable ? `Click to ${hasAccess ? 'remove' : 'grant'} ${dept}-${level} access` : ''}
                                >
                                  {hasAccess ? (
                                    <Eye className="h-4 w-4 text-green-600 mx-auto" />
                                  ) : (
                                    <EyeOff className="h-4 w-4 text-gray-300 mx-auto" />
                                  )}
                                </TableCell>
                              );
                            })
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold mb-2">Sensitivity Levels:</h4>
                    <div className="space-y-2 text-md3-body-small">
                      <div className="flex items-center gap-2">
                        <Badge className={getSensitivityColor(SENSITIVITY.PUBLIC)}>Public</Badge>
                        <span>Everyone can see</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getSensitivityColor(SENSITIVITY.INTERNAL)}>Internal</Badge>
                        <span>All logged-in users</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getSensitivityColor(SENSITIVITY.CONFIDENTIAL)}>Confidential</Badge>
                        <span>Management + specific roles</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getSensitivityColor(SENSITIVITY.RESTRICTED)}>Restricted</Badge>
                        <span>Admin + specific roles only</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Legend:</h4>
                    <div className="space-y-2 text-md3-body-small">
                      <div className="flex items-center gap-2">
                        <Eye className="h-4 w-4 text-green-600" />
                        <span>Can view field</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <EyeOff className="h-4 w-4 text-gray-300" />
                        <span>Field hidden/masked</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Action Permissions Matrix */}
          <TabsContent value="actions" className="space-y-6">
            <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
              <CardHeader>
                <CardTitle className="text-md3-title-large text-md3-on-surface ">Action Permissions Matrix</CardTitle>
              </CardHeader>
              <CardContent>
                {Object.entries(actionConfig).map(([module, actions]) => (
                  <div key={module} className="mb-8">
                    <h3 className="text-md3-title-large mb-4 capitalize">{module}</h3>
                    <div className="overflow-x-auto">
                      <Table className="bg-md3-surface-container rounded-md3-md">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[150px]">Action</TableHead>
                            {getFilteredDepartments().map(dept => (
                              <TableHead key={dept} colSpan={getFilteredLevels().length} className="text-center border-l">
                                {dept}
                              </TableHead>
                            ))}
                          </TableRow>
                          <TableRow>
                            <TableHead></TableHead>
                            {getFilteredDepartments().map(dept => (
                              getFilteredLevels().map(level => (
                                <TableHead key={`${dept}-${level}`} className="text-xs">
                                  {level === 'Team Member' ? 'TM' : level === 'Team Lead' ? 'TL' : level === 'Manager' ? 'MGR' : 'ADM'}
                                </TableHead>
                              ))
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.entries(actions).map(([action, config]) => (
                            <TableRow key={action}>
                              <TableCell className="font-medium capitalize">{action}</TableCell>
                              {getFilteredDepartments().map(dept => (
                                getFilteredLevels().map(level => {
                                  const hasPermission = checkActionPermission(module, action, dept, level);
                                  const isClickable = editMode && canEdit;
                                  return (
                                    <TableCell 
                                      key={`${action}-${dept}-${level}`} 
                                      className={`text-center ${isClickable ? 'cursor-pointer hover:bg-md3-surface-container' : ''}`}
                                      onClick={() => isClickable && handleActionCellClick(module, action, dept, level, actionConfig)}
                                      title={isClickable ? `Click to ${hasPermission ? 'remove' : 'grant'} ${dept}-${level} permission` : ''}
                                    >
                                      {hasPermission ? (
                                        <Check className="h-4 w-4 text-green-600 mx-auto" />
                                      ) : (
                                        <X className="h-4 w-4 text-red-400 mx-auto" />
                                      )}
                                    </TableCell>
                                  );
                                })
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Summary */}
          <TabsContent value="summary" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
                <CardHeader>
                  <CardTitle className="text-lg text-md3-title-medium text-md3-on-surface">Total Pages</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-md3-display-small text-md3-on-surface">{Object.keys(PAGE_ACCESS).length}</p>
                  <p className="text-md3-body-small text-md3-on-surface-variant mt-2">Protected pages configured</p>
                </CardContent>
              </Card>

              <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
                <CardHeader>
                  <CardTitle className="text-lg text-md3-title-medium text-md3-on-surface">Total Modules</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-md3-display-small text-md3-on-surface">{Object.keys(fieldConfig).length}</p>
                  <p className="text-md3-body-small text-md3-on-surface-variant mt-2">Modules with field controls</p>
                </CardContent>
              </Card>

              <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
                <CardHeader>
                  <CardTitle className="text-lg text-md3-title-medium text-md3-on-surface">Protected Fields</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-md3-display-small text-md3-on-surface">
                    {Object.values(fieldConfig).reduce((total, module) => 
                      total + Object.keys(module).length, 0
                    )}
                  </p>
                  <p className="text-md3-body-small text-md3-on-surface-variant mt-2">Fields under access control</p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
              <CardHeader>
                <CardTitle className="text-md3-title-large text-md3-on-surface ">System Overview</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-semibold mb-2">Departments:</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.values(DEPARTMENTS).map(dept => (
                      <Badge key={dept} variant="outline">{dept}</Badge>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-2">Access Levels:</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.values(ACCESS_LEVELS).map(level => (
                      <Badge key={level} variant="outline">{level}</Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Configuration Location:</h4>
                  <code className="text-md3-body-small bg-md3-surface-container px-2 py-1 rounded">
                    /app/frontend/src/config/permissions.js
                  </code>
                </div>

                <div className="mt-6 p-4 bg-blue-50 rounded-md3-lg">
                  <h4 className="font-semibold mb-2 text-blue-900">📘 Documentation</h4>
                  <p className="text-md3-body-small text-blue-800">
                    For detailed implementation guide and examples, see:
                    <br />
                    <code className="bg-md3-surface-container px-2 py-1 rounded mt-2 inline-block">
                      /app/PERMISSION_SYSTEM_GUIDE.md
                    </code>
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        )}
      </div>
    </Layout>
  );
}
